package fr.manyverse.app.presentation.viewmodel

import androidx.lifecycle.viewModelScope
import fr.manyverse.app.domain.model.ActiveLockpicking
import fr.manyverse.app.domain.model.LockpickingStatus
import fr.manyverse.app.domain.repository.LockpickingRepository
import fr.manyverse.app.domain.usecase.CancelLockpickingUseCase
import fr.manyverse.app.domain.usecase.CloseLockpickingUseCase
import fr.manyverse.app.domain.usecase.CreateLockpickingUseCase
import fr.manyverse.app.domain.usecase.TimeoutLockpickingUseCase
import fr.manyverse.app.domain.usecase.UnlockLockUseCase
import fr.manyverse.app.presentation.error.UserErrorNotifier
import fr.manyverse.app.presentation.state.AsyncUiState
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class LockpickingViewModel(
    private val createLockpickingUseCase: CreateLockpickingUseCase,
    private val unlockLockUseCase: UnlockLockUseCase,
    private val cancelLockpickingUseCase: CancelLockpickingUseCase,
    private val timeoutLockpickingUseCase: TimeoutLockpickingUseCase,
    private val closeLockpickingUseCase: CloseLockpickingUseCase,
    private val lockpickingRepository: LockpickingRepository,
    private val errorNotifier: UserErrorNotifier
) : BaseViewModel() {

    private val _launchState = MutableStateFlow<AsyncUiState<ActiveLockpicking>>(AsyncUiState.Idle)
    val launchState: StateFlow<AsyncUiState<ActiveLockpicking>> = _launchState.asStateFlow()

    private val _activeLockpicking = MutableStateFlow<ActiveLockpicking?>(null)
    val activeLockpicking: StateFlow<ActiveLockpicking?> = _activeLockpicking.asStateFlow()

    private val _isMinimized = MutableStateFlow(false)
    val isMinimized: StateFlow<Boolean> = _isMinimized.asStateFlow()

    private var observeJob: Job? = null
    private var observingTableId: String? = null
    private var observingPlayerId: String? = null
    private var observingLockpickingId: String? = null

    fun create(
        tableId: String,
        targetParticipantId: String,
        lockCount: Int,
        timeLimitSeconds: Int?,
        showLockCount: Boolean,
        soundEnabled: Boolean,
        createdBy: String
    ) {
        launchAsyncWithCallback(
            state = _launchState,
            block = {
                createLockpickingUseCase(
                    tableId = tableId,
                    targetParticipantId = targetParticipantId,
                    lockCount = lockCount,
                    timeLimitSeconds = timeLimitSeconds,
                    showLockCount = showLockCount,
                    soundEnabled = soundEnabled,
                    createdBy = createdBy
                )
            },
            onSuccess = { lockpicking ->
                _activeLockpicking.value = lockpicking
                startObservingById(lockpicking.id)
            }
        )
    }

    fun unlockNext(lockpicking: ActiveLockpicking) {
        viewModelScope.launch {
            unlockLockUseCase(lockpicking = lockpicking)
                .onSuccess { updated -> _activeLockpicking.value = updated }
                .onFailure(errorNotifier::notify)
        }
    }

    fun cancel(lockpickingId: String) {
        viewModelScope.launch {
            cancelLockpickingUseCase(lockpickingId = lockpickingId).onSuccess {
                _activeLockpicking.value = null
                _isMinimized.value = false
                observeJob?.cancel()
                observingLockpickingId = null
            }.onFailure(errorNotifier::notify)
        }
    }

    fun timeout(lockpickingId: String) {
        viewModelScope.launch {
            timeoutLockpickingUseCase(lockpickingId = lockpickingId).onFailure(errorNotifier::notify)
        }
    }

    fun close() {
        closeLockpickingUseCase()
        _activeLockpicking.value = null
        _isMinimized.value = false
    }

    fun minimize() {
        _isMinimized.value = true
    }

    fun restore() {
        _isMinimized.value = false
    }

    fun startObservingForPlayer(tableId: String, playerId: String) {
        if (observeJob?.isActive == true && observingTableId == tableId && observingPlayerId == playerId) {
            return
        }
        observeJob?.cancel()
        observingTableId = tableId
        observingPlayerId = playerId
        observeJob = viewModelScope.launch {
            val preExistingId = lockpickingRepository.getActiveLockpickingForPlayer(tableId, playerId)
                .getOrNull()
                ?.also { existing ->
                    _activeLockpicking.value = existing
                    _isMinimized.value = true
                }?.id

            lockpickingRepository.observeActiveLockpickingForPlayer(tableId, playerId)
                .collect { lockpicking ->
                    when {
                        lockpicking == null || lockpicking.status == LockpickingStatus.CANCELLED -> {
                            _activeLockpicking.value = null
                            _isMinimized.value = false
                        }
                        lockpicking.status != LockpickingStatus.ACTIVE && _isMinimized.value -> {
                            _activeLockpicking.value = null
                            _isMinimized.value = false
                        }
                        else -> {
                            val isNew = lockpicking.id != preExistingId && _activeLockpicking.value?.id != lockpicking.id
                            if (isNew) _isMinimized.value = false
                            _activeLockpicking.value = lockpicking
                        }
                    }
                }
        }
    }

    /**
     * Re-sync léger au retour en foreground : refetch sans recréer la souscription Realtime.
     */
    fun refreshForPlayer(tableId: String, playerId: String) {
        viewModelScope.launch {
            lockpickingRepository.getActiveLockpickingForPlayer(tableId, playerId).onSuccess { lockpicking ->
                if (lockpicking == null || _activeLockpicking.value?.id == lockpicking.id) return@onSuccess
                _activeLockpicking.value = lockpicking
                _isMinimized.value = false
            }
        }
    }

    fun startObservingForGm(tableId: String) {
        viewModelScope.launch {
            lockpickingRepository.getActiveLockpickingForTable(tableId)
                .onSuccess { existing ->
                    if (existing != null) {
                        _activeLockpicking.value = existing
                        // Évite de re-souscrire au canal Realtime si l'on observe déjà ce lockpicking
                        if (observingLockpickingId != existing.id || observeJob?.isActive != true) {
                            startObservingById(existing.id)
                        }
                    }
                }
        }
    }

    private fun startObservingById(lockpickingId: String) {
        observeJob?.cancel()
        observingLockpickingId = lockpickingId
        observeJob = viewModelScope.launch {
            lockpickingRepository.observeLockpicking(lockpickingId).collect { lockpicking ->
                _activeLockpicking.value = lockpicking
            }
        }
    }

    fun resetLaunchState() {
        _launchState.value = AsyncUiState.Idle
    }

    override fun onCleared() {
        super.onCleared()
        observeJob?.cancel()
    }
}
